import type { DeviceType } from '../pages/admin/devices/deviceTypes';
import { DEVICE_TYPES } from '../pages/admin/devices/deviceTypes';
import { deviceMatchesSectionPlacement, placementForFieldSection } from './devicePlacement';

const DEVICE_TYPE_SET = new Set<string>(DEVICE_TYPES);

const SYSTEM_ONLY_SECTION =
    /control\s*unit|facu|monitoring|documentation|summary|power\s*supply|annunciator|transponder|deficiency\s*log|^20\s|(^21\s|^22\.)/i;

/** Map device-registry category headings ("Smoke Detector — Hall") to a single asset type. */
function deviceTypeFromRegistryHeading(categoryLabel: string): DeviceType | null {
    const head = categoryLabel.split('—')[0]?.trim() || categoryLabel.trim();
    const rules: Array<{ test: RegExp; type: DeviceType }> = [
        { test: /^smoke\s*detector\b/i, type: 'smoke_detector' },
        { test: /^heat\s*detector\b/i, type: 'heat_detector' },
        { test: /^(pull|manual)\s*station\b/i, type: 'pull_station' },
        { test: /^horn\s*strobe\b/i, type: 'horn_strobe' },
        { test: /^fire\s*extinguisher\b/i, type: 'fire_extinguisher' },
        { test: /^emergency\s*light\b/i, type: 'emergency_light' },
        { test: /^exit\s*sign\b/i, type: 'exit_sign' },
        { test: /^sprinkler\s*head\b/i, type: 'sprinkler_head' },
        { test: /^flow\s*switch\b/i, type: 'flow_switch' },
        { test: /^tamper\s*switch\b/i, type: 'tamper_switch' },
        { test: /^control\s*panel\b/i, type: 'control_panel' },
        { test: /^backflow/i, type: 'backflow_preventer' },
        { test: /^suppression/i, type: 'suppression_system' },
        { test: /^fire\s*door\b/i, type: 'fire_door' },
        { test: /^damper\b/i, type: 'damper' }
    ];
    for (const rule of rules) {
        if (rule.test.test(head)) return rule.type;
    }
    return null;
}

/** Infer allowed types from checklist line labels inside a section (S536 combined sections). */
function deviceTypesFromItemLabels(labels: string[]): DeviceType[] {
    const hay = labels.join(' ').toLowerCase();
    const matched = new Set<DeviceType>();

    const rules: Array<{ test: RegExp; types: DeviceType[] }> = [
        { test: /heat\s*detector/, types: ['heat_detector'] },
        { test: /smoke\s*detector|smoke\s*alarm/, types: ['smoke_detector'] },
        { test: /speaker|notification|horn|strobe/, types: ['horn_strobe'] },
        { test: /pull\s*station|manual\s*station/, types: ['pull_station'] },
        { test: /flow\s*switch|waterflow/, types: ['flow_switch'] },
        { test: /tamper/, types: ['tamper_switch'] },
        { test: /sprinkler/, types: ['sprinkler_head', 'flow_switch', 'tamper_switch'] },
        { test: /extinguisher/, types: ['fire_extinguisher'] },
        { test: /hose\s*cabinet|hose/, types: ['fire_extinguisher', 'other'] },
        { test: /emergency\s*light/, types: ['emergency_light'] },
        { test: /exit\s*sign/, types: ['exit_sign'] },
        { test: /duct\s*smoke/, types: ['smoke_detector'] },
        { test: /\bco\s*detector/, types: ['smoke_detector'] },
        { test: /wireless/, types: ['smoke_detector', 'heat_detector', 'pull_station', 'horn_strobe'] },
        { test: /air\s*sampling/, types: ['smoke_detector'] }
    ];

    for (const rule of rules) {
        if (rule.test.test(hay)) {
            rule.types.forEach((t) => matched.add(t));
        }
    }

    return Array.from(matched);
}

/** S536 / OFC / legacy sections where technicians inspect devices one-by-one. */
export function isFieldDeviceSection(categoryLabel: string): boolean {
    const cat = categoryLabel.trim();
    if (!cat || cat === 'General') return false;

    if (deviceTypeFromRegistryHeading(cat)) return true;

    if (/^23 — Field Devices/i.test(cat)) return true;
    if (/Emergency Lighting/i.test(cat)) return true;
    if (/Fire Extinguishers|Hose Cabinets/i.test(cat)) return true;

    const legacyFieldPatterns = [
        /smoke\s*detector/i,
        /heat\s*detector/i,
        /manual\s*station/i,
        /pull\s*station/i,
        /notification\s*appliance/i,
        /waterflow/i,
        /supervisory\s*device/i,
        /duct\s*smoke/i,
        /\bco\s*detector/i,
        /wireless\s*device/i,
        /air\s*sampling/i,
        /sprinkler/i,
        /flow\s*switch/i,
        /tamper\s*switch/i,
        /emergency\s*light/i,
        /exit\s*sign/i,
        /extinguisher/i,
        /hose\s*cabinet/i
    ];
    if (legacyFieldPatterns.some((re) => re.test(cat))) return true;

    if (SYSTEM_ONLY_SECTION.test(cat)) return false;

    return false;
}

/**
 * Strict device-type filter for the picker on this section page.
 * Smoke sections → smoke_detector only; heat sections → heat_detector only; etc.
 */
export function deviceTypesForFieldSection(categoryLabel: string, itemLabels: string[] = []): DeviceType[] {
    const cat = categoryLabel.trim();

    const registryType = deviceTypeFromRegistryHeading(cat);
    if (registryType) return [registryType];

    const fromLabels = deviceTypesFromItemLabels(itemLabels);
    if (fromLabels.length === 1) return fromLabels;

    const categoryRules: Array<{ test: RegExp; types: DeviceType[] }> = [
        { test: /in-suite smoke alarms/i, types: ['smoke_detector'] },
        { test: /smoke\s*detector|smoke\s*alarm/i, types: ['smoke_detector'] },
        { test: /duct\s*smoke/i, types: ['smoke_detector'] },
        { test: /air\s*sampling/i, types: ['smoke_detector'] },
        { test: /\bco\s*detector/i, types: ['smoke_detector'] },
        { test: /heat\s*detector/i, types: ['heat_detector'] },
        { test: /manual\s*station|pull\s*station/i, types: ['pull_station'] },
        { test: /notification\s*appliance|horn|strobe|speaker/i, types: ['horn_strobe'] },
        { test: /waterflow/i, types: ['flow_switch'] },
        { test: /flow\s*switch/i, types: ['flow_switch'] },
        { test: /supervisory\s*device/i, types: ['tamper_switch'] },
        { test: /tamper\s*switch/i, types: ['tamper_switch'] },
        { test: /sprinkler\s*head/i, types: ['sprinkler_head'] },
        { test: /sprinkler\s*devices/i, types: ['flow_switch', 'tamper_switch', 'sprinkler_head'] },
        { test: /emergency\s*light/i, types: ['emergency_light'] },
        { test: /exit\s*sign/i, types: ['exit_sign'] },
        { test: /extinguisher/i, types: ['fire_extinguisher'] },
        { test: /hose\s*cabinet|hose/i, types: ['other'] },
        { test: /fire extinguishers & hose/i, types: ['fire_extinguisher', 'other'] },
        { test: /common areas/i, types: ['smoke_detector', 'heat_detector', 'pull_station', 'horn_strobe'] },
        { test: /in-suite fire alarm/i, types: ['heat_detector', 'horn_strobe'] },
        { test: /wireless\s*device/i, types: ['smoke_detector', 'heat_detector', 'pull_station', 'horn_strobe'] }
    ];

    for (const rule of categoryRules) {
        if (rule.test.test(cat)) {
            return rule.types;
        }
    }

    if (fromLabels.length > 0) {
        return fromLabels;
    }

    return [];
}

export function deviceMatchesSectionTypes(deviceType: string | undefined, allowed: DeviceType[]): boolean {
    if (!allowed.length) return false;
    if (!deviceType) return false;
    return DEVICE_TYPE_SET.has(deviceType) && allowed.includes(deviceType as DeviceType);
}

export function overallResultFromLines(
    lines: Array<{ result?: string | null }>
): 'pass' | 'fail' | 'n/a' {
    const results = lines.map((l) => String(l.result || '').toLowerCase()).filter(Boolean);
    if (results.length === 0) return 'n/a';
    if (results.some((r) => r === 'fail' || r === 'deficient')) return 'fail';
    if (results.every((r) => r === 'n/a')) return 'n/a';
    if (results.some((r) => r === 'pass')) return 'pass';
    return 'n/a';
}

type FieldSectionDevice = {
    _id: string;
    type?: string;
    placement?: string | null;
    zone?: string | null;
    installationLocation?: string | null;
};

/** Devices that belong in this §23 field-device section (type + in-suite/common filter). */
export function matchingDevicesForFieldSection(
    sectionTitle: string,
    itemLabels: string[],
    devices: FieldSectionDevice[]
): FieldSectionDevice[] {
    const allowedTypes = deviceTypesForFieldSection(sectionTitle, itemLabels);
    const placement = placementForFieldSection(sectionTitle);
    return devices.filter(
        (d) => deviceMatchesSectionTypes(d.type, allowedTypes) && deviceMatchesSectionPlacement(d, placement)
    );
}

export function fieldSectionIsComplete(
    sectionTitle: string,
    itemLabels: string[],
    sectionItems: Array<{ itemId?: string; result?: string | null }>,
    devices: FieldSectionDevice[],
    records: Array<{ sectionKey: string; deviceId?: string | { _id?: string } | null }>,
    sectionKey: string
): boolean {
    const required = matchingDevicesForFieldSection(sectionTitle, itemLabels, devices);
    if (required.length === 0) {
        const inspectable = sectionItems.filter((i) => i.itemId !== 'NEW-BUILDING-NOTICE');
        return inspectable.length > 0 && inspectable.every((i) => i.result);
    }
    const logged = new Set(
        records
            .filter((r) => r.sectionKey === sectionKey)
            .map((r) => {
                const raw = r.deviceId;
                if (!raw) return '';
                if (typeof raw === 'string') return raw;
                return String(raw._id || '');
            })
            .filter(Boolean)
    );
    return required.every((d) => logged.has(d._id));
}
